import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { nutritionApi } from '@/lib/api';
import { setPageArea, reportError } from '@/lib/sentry';
import { sanitizePhone, COUNTRY_CODES } from '@/lib/phone-utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  CalendarDays,
  Clock,
  User,
  Phone,
  Mail,
  Check,
  ArrowRight,
  ArrowLeft,
  Loader2,
  CalendarPlus,
  Share2,
  Building2,
  Sparkles,
  AlertCircle,
} from 'lucide-react';

type Step = 1 | 2 | 3 | 4;

interface BookingInfo {
  success: boolean;
  error?: string;
  gym_id: string;
  name: string;
  slug: string;
  timezone: string;
  booking_enabled: boolean;
  nutritionist_name: string;
  slot_minutes: number;
  advance_hours: number;
  max_days: number;
  cancellation_policy: string;
}

const CONSULTATION_TYPES = [
  'Primera consulta',
  'Seguimiento',
  'Control de peso',
  'Plan de alimentación',
  'Otro',
];

const MONTH_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const WEEKDAY_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

/** Fecha local en formato YYYY-MM-DD (sin desfase por zona horaria). */
const toDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const buildIcs = (opts: { start: Date; end: Date; title: string; description: string }) => {
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Gym SaaS//Nutricion//ES',
    'BEGIN:VEVENT',
    `UID:${Date.now()}@gym-saas`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(opts.start)}`,
    `DTEND:${fmt(opts.end)}`,
    `SUMMARY:${opts.title}`,
    `DESCRIPTION:${opts.description}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
};

export default function NutritionBookingPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [info, setInfo] = useState<BookingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>(1);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [slots, setSlots] = useState<string[]>([]);
  const [slotsLoadedFor, setSlotsLoadedFor] = useState<string>('');

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    type: CONSULTATION_TYPES[0],
    notes: '',
  });
  const [countryCode, setCountryCode] = useState('+503');

  const [confirmation, setConfirmation] = useState<{
    scheduledAt: string;
    durationMinutes: number;
    nutritionistName: string;
  } | null>(null);

  useEffect(() => {
    setPageArea('onboarding');
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!slug) return;
      setIsLoading(true);
      setLoadError(null);
      try {
        const res = await nutritionApi.getPublicBookingInfo(slug);
        if (!res.success || !res.info || res.info.success === false) {
          setLoadError(res.info?.error || 'No se pudo cargar la agenda.');
          return;
        }
        setInfo(res.info as BookingInfo);
      } catch (error: any) {
        reportError(error, { operation: 'nutrition_booking_info', area: 'onboarding' });
        setLoadError(error?.message || 'No se pudo cargar la agenda.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [slug]);

  // Días disponibles para reservar (hoy → max_days)
  const availableDates = useMemo(() => {
    if (!info) return [];
    const maxDays = Math.min(info.max_days || 60, 90);
    const today = new Date();
    return Array.from({ length: maxDays }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      return date;
    });
  }, [info]);

  const loadSlots = async (dateKey: string) => {
    if (!info) return;
    setSelectedDate(dateKey);
    setSelectedSlot('');
    if (slotsLoadedFor === dateKey) return;

    setIsLoadingSlots(true);
    try {
      const res = await nutritionApi.getPublicSlots(info.gym_id, dateKey);
      if (res.success) {
        setSlots(Array.isArray(res.slots) ? res.slots : []);
        setSlotsLoadedFor(dateKey);
      } else {
        setSlots([]);
      }
    } catch (error: any) {
      reportError(error, { operation: 'nutrition_booking_slots', area: 'onboarding' });
      toast.error('No se pudieron cargar los horarios.');
      setSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: info?.timezone || undefined,
    });

  const handleNext = () => {
    if (step === 1 && !selectedSlot) {
      toast.error('Selecciona un horario disponible.');
      return;
    }
    if (step === 2 && (!form.name.trim() || !form.phone.trim())) {
      toast.error('Ingresa tu nombre y teléfono.');
      return;
    }
    setStep((s) => (s === 3 ? 3 : ((s + 1) as Step)));
  };

  const handleBack = () => setStep((s) => (s === 1 ? 1 : ((s - 1) as Step)));

  const handleSubmit = async () => {
    if (!info || !selectedSlot) return;
    setIsSubmitting(true);
    try {
      const res = await nutritionApi.schedulePublicAppointment({
        gym_id: info.gym_id,
        name: form.name.trim(),
        phone: sanitizePhone(form.phone, countryCode),
        email: form.email.trim() || null,
        scheduled_at: selectedSlot,
        type: form.type || null,
        notes: form.notes.trim() || null,
      });

      if (!res.success) {
        toast.error(res.error || 'No se pudo agendar la cita.');
        // El horario pudo tomarlo otra persona: recargamos los slots.
        setSlotsLoadedFor('');
        await loadSlots(selectedDate);
        return;
      }

      setConfirmation({
        scheduledAt: res.result.scheduled_at,
        durationMinutes: res.result.duration_minutes ?? info.slot_minutes,
        nutritionistName: res.result.nutritionist_name || info.nutritionist_name,
      });
      setStep(4);
      toast.success('¡Cita agendada!');
    } catch (error: any) {
      reportError(error, { operation: 'nutrition_booking_submit', area: 'onboarding' });
      toast.error('No se pudo agendar la cita. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const appointmentDates = () => {
    if (!confirmation) return null;
    const start = new Date(confirmation.scheduledAt);
    const end = new Date(start.getTime() + confirmation.durationMinutes * 60000);
    return { start, end };
  };

  const downloadIcs = () => {
    if (!confirmation || !info) return;
    const dates = appointmentDates();
    if (!dates) return;
    const ics = buildIcs({
      start: dates.start,
      end: dates.end,
      title: `Cita de nutrición · ${info.name}`,
      description: `${confirmation.nutritionistName || 'Nutrición'} · ${info.name}`,
    });
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cita-nutricion.ics';
    a.click();
    URL.revokeObjectURL(url);
  };

  const googleCalendarUrl = () => {
    if (!confirmation || !info) return '';
    const dates = appointmentDates();
    if (!dates) return '';
    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const text = encodeURIComponent(`Cita de nutrición · ${info.name}`);
    const details = encodeURIComponent(
      `${confirmation.nutritionistName || 'Nutrición'} · ${info.name}`
    );
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${fmt(
      dates.start
    )}/${fmt(dates.end)}&details=${details}`;
  };

  const shareWhatsapp = () => {
    if (!confirmation || !info) return;
    const when = new Date(confirmation.scheduledAt).toLocaleString('es-ES', {
      dateStyle: 'full',
      timeStyle: 'short',
      timeZone: info.timezone || undefined,
    });
    const text = encodeURIComponent(
      `¡Agendé mi cita de nutrición en ${info.name}! ${when}.`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  // ---- Estados de carga / error ----
  if (isLoading) {
    return (
      <div className="min-h-screen relative flex flex-col items-center justify-center overflow-hidden">
        <div className="fixed inset-0 z-0">
          <img src="/hero-vibrant.png" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-lg" />
        </div>
        <div className="relative z-10 flex flex-col items-center">
          <div className="mb-8 flex h-20 w-20 animate-pulse items-center justify-center rounded-3xl border border-orange-500/30 bg-orange-600/20">
            <Loader2 className="h-10 w-10 animate-spin text-orange-500" />
          </div>
          <p className="animate-pulse text-sm font-black uppercase tracking-[0.3em] text-white">
            Cargando agenda...
          </p>
        </div>
      </div>
    );
  }

  if (loadError || !info) {
    return (
      <div className="min-h-screen relative flex flex-col items-center justify-center overflow-hidden p-6">
        <div className="fixed inset-0 z-0">
          <img src="/hero-vibrant.png" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-lg" />
        </div>
        <div className="relative z-10 w-full max-w-md text-center">
          <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-[2.5rem] border border-red-500/20 bg-red-500/10 backdrop-blur-xl">
            <Building2 className="h-12 w-12 text-red-500" />
          </div>
          <h1 className="mb-4 text-4xl font-black tracking-tighter text-white">
            Agenda no disponible
          </h1>
          <p className="mb-10 text-lg font-medium leading-relaxed text-slate-400">
            {loadError || 'El enlace no es válido o la agenda está temporalmente cerrada.'}
          </p>
          <Button
            onClick={() => navigate('/')}
            className="h-16 w-full rounded-2xl border border-white/10 bg-white/10 text-lg font-black text-white backdrop-blur-md hover:bg-white/20"
          >
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  if (!info.booking_enabled) {
    return (
      <div className="min-h-screen relative flex flex-col items-center justify-center overflow-hidden p-6">
        <div className="fixed inset-0 z-0">
          <img src="/hero-vibrant.png" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-lg" />
        </div>
        <div className="relative z-10 w-full max-w-md text-center">
          <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-[2.5rem] border border-amber-500/20 bg-amber-500/10 backdrop-blur-xl">
            <AlertCircle className="h-12 w-12 text-amber-400" />
          </div>
          <h1 className="mb-4 text-4xl font-black tracking-tighter text-white">
            Reservas cerradas
          </h1>
          <p className="mb-10 text-lg font-medium leading-relaxed text-slate-400">
            {info.name} todavía no tiene activa la reserva de citas en línea. Comunícate directamente
            con el gimnasio.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="h-16 w-full rounded-2xl border border-white/10 bg-white/10 text-lg font-black text-white backdrop-blur-md hover:bg-white/20"
          >
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const steps = [
    { step: 1, label: 'Fecha y hora', desc: 'Disponibilidad' },
    { step: 2, label: 'Tus datos', desc: 'Identificación' },
    { step: 3, label: 'Confirmar', desc: 'Reserva' },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden selection:bg-orange-500/30">
      <div className="fixed inset-0 z-0">
        <img src="/hero-vibrant.png" alt="" className="h-full w-full scale-105 object-cover" />
        <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[5px]" />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-600/20 via-transparent to-slate-950/80" />
      </div>

      <div className="relative z-10 mx-auto max-w-4xl px-6 py-12 md:py-20">
        <div className="mb-10 text-center animate-in fade-in slide-in-from-top duration-700">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-orange-500/20 bg-orange-500/10 px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-orange-400 backdrop-blur-md">
            <Sparkles className="h-3 w-3" /> Reserva de citas
          </div>
          <h1 className="mb-3 text-4xl font-black tracking-tighter text-white md:text-6xl">
            {info.name}
          </h1>
          {info.nutritionist_name && (
            <p className="text-lg font-medium text-slate-300">
              Agenda con <span className="font-bold text-white">{info.nutritionist_name}</span>
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          {/* Stepper lateral */}
          {step < 4 && (
            <div className="hidden flex-col gap-8 pt-4 lg:col-span-3 lg:flex">
              {steps.map((s) => (
                <div
                  key={s.step}
                  className={cn(
                    'flex items-center gap-4 transition-all duration-500',
                    step >= s.step ? 'opacity-100' : 'opacity-30'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border-2 font-black transition-all',
                      step === s.step
                        ? 'scale-110 border-orange-400 bg-orange-600 text-white shadow-xl shadow-orange-600/30'
                        : step > s.step
                          ? 'border-orange-600/40 bg-orange-600/20 text-orange-400'
                          : 'border-slate-700/50 bg-slate-800/50 text-slate-500'
                    )}
                  >
                    {step > s.step ? <Check className="h-6 w-6 stroke-[3px]" /> : s.step}
                  </div>
                  <div className="flex flex-col">
                    <span className="mb-1.5 text-sm font-black leading-none text-white">{s.label}</span>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      {s.desc}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={cn(step < 4 ? 'lg:col-span-9' : 'lg:col-span-12')}>
            <div className="overflow-hidden rounded-[2.5rem] border-none bg-white/95 shadow-2xl backdrop-blur-xl">
              <div className="h-3 bg-gradient-to-r from-orange-600 to-amber-500" />
              <div className="p-6 md:p-10">
                {/* Paso 1 — fecha y hora */}
                {step === 1 && (
                  <div className="space-y-6 animate-in">
                    <div>
                      <Label className="mb-3 block text-xs font-black uppercase tracking-widest text-slate-400">
                        Elige el día
                      </Label>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {availableDates.map((date) => {
                          const key = toDateKey(date);
                          const isSelected = selectedDate === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => loadSlots(key)}
                              className={cn(
                                'flex min-w-[64px] shrink-0 flex-col items-center rounded-2xl border-2 px-3 py-2 transition-all',
                                isSelected
                                  ? 'border-orange-600 bg-orange-50'
                                  : 'border-slate-100 bg-slate-50 hover:border-orange-300'
                              )}
                            >
                              <span className="text-[10px] font-black uppercase text-slate-400">
                                {WEEKDAY_SHORT[date.getDay()]}
                              </span>
                              <span className="text-lg font-black text-slate-900">{date.getDate()}</span>
                              <span className="text-[10px] font-bold uppercase text-slate-400">
                                {MONTH_SHORT[date.getMonth()]}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <Label className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-400">
                        <Clock className="h-4 w-4" /> Horarios disponibles
                      </Label>

                      {!selectedDate && (
                        <p className="rounded-2xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-400">
                          Selecciona un día para ver los horarios.
                        </p>
                      )}

                      {selectedDate && isLoadingSlots && (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                        </div>
                      )}

                      {selectedDate && !isLoadingSlots && slots.length === 0 && (
                        <p className="rounded-2xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-400">
                          No hay horarios disponibles ese día. Prueba con otra fecha.
                        </p>
                      )}

                      {selectedDate && !isLoadingSlots && slots.length > 0 && (
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                          {slots.map((slot) => (
                            <button
                              key={slot}
                              type="button"
                              onClick={() => setSelectedSlot(slot)}
                              className={cn(
                                'rounded-xl border-2 px-2 py-3 text-sm font-bold transition-all',
                                selectedSlot === slot
                                  ? 'border-orange-600 bg-orange-600 text-white'
                                  : 'border-slate-100 bg-slate-50 text-slate-700 hover:border-orange-300'
                              )}
                            >
                              {formatTime(slot)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Paso 2 — datos */}
                {step === 2 && (
                  <div className="space-y-6 animate-in">
                    <div className="space-y-3">
                      <Label
                        htmlFor="bk-name"
                        className="text-xs font-black uppercase tracking-widest text-slate-400"
                      >
                        Nombre completo
                      </Label>
                      <div className="group relative">
                        <User className="absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-orange-500" />
                        <Input
                          id="bk-name"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Nombre y Apellidos"
                          className="h-14 rounded-2xl border-slate-100 bg-slate-50 pl-14 text-lg font-semibold text-slate-900 focus:border-orange-500/30 focus:ring-4 focus:ring-orange-500/10"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label
                        htmlFor="bk-phone"
                        className="text-xs font-black uppercase tracking-widest text-slate-400"
                      >
                        Teléfono (WhatsApp)
                      </Label>
                      <div className="flex gap-2">
                        <Select value={countryCode} onValueChange={setCountryCode}>
                          <SelectTrigger className="h-14 w-[100px] rounded-2xl border-slate-100 bg-slate-50 px-3 text-sm font-bold text-slate-900">
                            <SelectValue placeholder="País" />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRY_CODES.map((c) => (
                              <SelectItem key={c.code} value={c.code}>
                                <span className="flex items-center gap-2">
                                  <span className="text-lg">{c.flag}</span>
                                  <span className="text-xs font-semibold">{c.code}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="group relative flex-1">
                          <Phone className="absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-orange-500" />
                          <Input
                            id="bk-phone"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            placeholder="8978 8978"
                            className="h-14 rounded-2xl border-slate-100 bg-slate-50 pl-14 text-lg font-semibold text-slate-900 focus:border-orange-500/30 focus:ring-4 focus:ring-orange-500/10"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label
                        htmlFor="bk-email"
                        className="text-xs font-black uppercase tracking-widest text-slate-400"
                      >
                        Email (opcional)
                      </Label>
                      <div className="group relative">
                        <Mail className="absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-orange-500" />
                        <Input
                          id="bk-email"
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="ejemplo@correo.com"
                          className="h-14 rounded-2xl border-slate-100 bg-slate-50 pl-14 text-lg font-semibold text-slate-900 focus:border-orange-500/30 focus:ring-4 focus:ring-orange-500/10"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Paso 3 — confirmar */}
                {step === 3 && (
                  <div className="space-y-6 animate-in">
                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <p className="mb-1 text-xs font-black uppercase tracking-widest text-slate-400">
                        Tu cita
                      </p>
                      <p className="text-lg font-bold text-slate-900">
                        {selectedSlot &&
                          new Date(selectedSlot).toLocaleString('es-ES', {
                            dateStyle: 'full',
                            timeStyle: 'short',
                            timeZone: info.timezone || undefined,
                          })}
                      </p>
                      <p className="text-sm text-slate-500">
                        {form.name} · {sanitizePhone(form.phone, countryCode)}
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-xs font-black uppercase tracking-widest text-slate-400">
                          Tipo de consulta
                        </Label>
                        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                          <SelectTrigger className="h-12 rounded-2xl border-slate-100 bg-slate-50 font-semibold">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CONSULTATION_TYPES.map((t) => (
                              <SelectItem key={t} value={t}>
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-black uppercase tracking-widest text-slate-400">
                          Notas (opcional)
                        </Label>
                        <Textarea
                          rows={1}
                          value={form.notes}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          placeholder="Motivo de la consulta"
                          className="rounded-2xl border-slate-100 bg-slate-50"
                        />
                      </div>
                    </div>

                    {info.cancellation_policy && (
                      <p className="rounded-2xl bg-amber-50 px-4 py-3 text-xs text-amber-700">
                        <strong>Política de cancelación:</strong> {info.cancellation_policy}
                      </p>
                    )}
                  </div>
                )}

                {/* Paso 4 — confirmación */}
                {step === 4 && confirmation && (
                  <div className="space-y-6 text-center animate-in">
                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                      <Check className="h-10 w-10 text-green-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-black tracking-tight text-slate-900">
                        ¡Cita confirmada!
                      </h2>
                      <p className="mt-1 text-sm text-slate-500">
                        Te esperamos en {info.name}
                        {confirmation.nutritionistName ? ` con ${confirmation.nutritionistName}` : ''}.
                      </p>
                    </div>

                    <div className="mx-auto max-w-sm rounded-2xl border border-slate-100 bg-slate-50 p-4 text-left">
                      <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
                        <CalendarDays className="h-4 w-4 text-orange-600" />
                        {new Date(confirmation.scheduledAt).toLocaleString('es-ES', {
                          dateStyle: 'full',
                          timeStyle: 'short',
                          timeZone: info.timezone || undefined,
                        })}
                      </p>
                      <p className="mt-1 pl-6 text-xs text-slate-500">
                        Duración: {confirmation.durationMinutes} min
                      </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-2">
                      <Button variant="outline" onClick={downloadIcs} className="rounded-xl">
                        <CalendarPlus className="mr-2 h-4 w-4" /> .ics
                      </Button>
                      <Button variant="outline" onClick={() => window.open(googleCalendarUrl(), '_blank')} className="rounded-xl">
                        <CalendarDays className="mr-2 h-4 w-4" /> Google Calendar
                      </Button>
                      <Button variant="outline" onClick={shareWhatsapp} className="rounded-xl">
                        <Share2 className="mr-2 h-4 w-4" /> Compartir
                      </Button>
                    </div>
                  </div>
                )}

                {/* Navegación */}
                {step < 4 && (
                  <div className="mt-8 flex items-center justify-between gap-3 border-t border-slate-100 pt-6">
                    {step > 1 ? (
                      <Button variant="ghost" onClick={handleBack} className="rounded-xl text-slate-500">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Atrás
                      </Button>
                    ) : (
                      <span />
                    )}
                    {step < 3 ? (
                      <Button
                        onClick={handleNext}
                        className="h-12 rounded-xl bg-orange-600 px-6 font-bold text-white hover:bg-orange-700"
                      >
                        Continuar <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="h-12 rounded-xl bg-orange-600 px-6 font-bold text-white hover:bg-orange-700"
                      >
                        {isSubmitting ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="mr-2 h-4 w-4" />
                        )}
                        Confirmar cita
                      </Button>
                    )}
                  </div>
                )}

                {step === 4 && (
                  <div className="mt-8 border-t border-slate-100 pt-6 text-center">
                    <Button variant="ghost" onClick={() => navigate('/')} className="rounded-xl text-slate-500">
                      Volver al inicio
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
